import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
// Layout components
import { AppLayout, AuthLayout } from './components/layouts';
// Domain pages â€” authorities
import AdminAuthorities from './domains/authorities/pages/AdminAuthorities';
import AdminAuthorityDetail from './domains/authorities/pages/AdminAuthorityDetail';
import AdminDataModel from './domains/authorities/pages/AdminDataModel';
import AdminScraperDetail from './domains/authorities/pages/AdminScraperDetail';
import AdminScrapers from './domains/authorities/pages/AdminScrapers';
import AdminSubmissions from './domains/authorities/pages/AdminSubmissions';
// Domain pages â€” elections
import BallotResults from './domains/elections/pages/BallotResults';
import ElectionDetail from './domains/elections/pages/ElectionDetail';
import VoteLocation from './domains/elections/pages/VoteLocation';
import VoterRegistration from './domains/elections/pages/VoterRegistration';
import VoteState from './domains/elections/pages/VoteState';

// Lazy-load VoteSearchResults to prevent PlaceMap/Leaflet errors on initial load
const VoteSearchResults = lazy(() => import('./domains/elections/pages/VoteSearchResults'));
// Domain pages â€” geography
import Counties from './domains/geography/pages/Counties';
import CountyDetail from './domains/geography/pages/CountyDetail';
import DistrictDetail from './domains/geography/pages/DistrictDetail';
// Domain pages â€” identity
import Login from './domains/identity/pages/Login';
import Settings from './domains/identity/pages/Settings';
import Signup from './domains/identity/pages/Signup';
import Subscription from './domains/identity/pages/Subscription';
// Store
import { useAuthStore } from './domains/identity/store/authStore';
// Domain pages â€” outreach
import MailerSetupPage from './domains/outreach/pages/MailerSetupPage';
import MailerSuccessPage from './domains/outreach/pages/MailerSuccessPage';
import MyDeliveryPage from './domains/outreach/pages/MyDeliveryPage';
// Pages without a domain (general)
import Accessibility from './pages/Accessibility';
import Dashboard from './pages/Dashboard';
import Elections from './pages/Elections';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/privacy_policy_page';
import TermsOfService from './pages/terms_of_service_page';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Block render until session check is complete to prevent flash of logged-out state
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-600">
        Loading VotePlanâ€¦
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Public routes */}
            <Route element={<AppLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/ballot" element={<BallotResults />} />
              <Route path="/elections" element={<Elections />} />
              <Route path="/elections/:id" element={<ElectionDetail />} />
              <Route path="/counties" element={<Counties />} />
              <Route path="/counties/:state/:county" element={<CountyDetail />} />
              <Route
                path="/vote"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <VoteSearchResults />
                  </Suspense>
                }
              />
              <Route path="/vote/:state" element={<VoteState />} />
              <Route path="/vote/:state/:location" element={<VoteLocation />} />
              <Route
                path="/district/congressional/:state/:label"
                element={<DistrictDetail districtType="congressional" />}
              />
              <Route
                path="/district/state/upperhouse/:label"
                element={<DistrictDetail districtType="senate" />}
              />
              <Route
                path="/district/state/lowerhouse/:label"
                element={<DistrictDetail districtType="house" />}
              />
              <Route
                path="/district/school/:label"
                element={<DistrictDetail districtType="school" />}
              />
            </Route>

            {/* Auth routes (login/signup) */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
            </Route>

            {/* Admin routes */}
            <Route element={<AppLayout />}>
              <Route path="/admin/submissions" element={<AdminSubmissions />} />
              <Route path="/admin/authorities" element={<AdminAuthorities />} />
              <Route path="/admin/authorities/:id" element={<AdminAuthorityDetail />} />
              <Route path="/admin/scrapers" element={<AdminScrapers />} />
              <Route path="/admin/scrapers/:state" element={<AdminScraperDetail />} />
              <Route path="/admin/data-model" element={<AdminDataModel />} />
            </Route>

            {/* Protected routes */}
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/deliveries" element={<MyDeliveryPage />} />
              <Route path="/mailers/setup" element={<MailerSetupPage />} />
              <Route path="/mailers/success" element={<MailerSuccessPage />} />
              <Route path="/registration" element={<VoterRegistration />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Routes>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
